import React, { useMemo, useEffect } from 'react';
import { RootCauseGroupResult } from 'libs/end-to-end/src/root-cause-analysis';
import { extractRootCauseTextOutputs } from 'libs/end-to-end/src/root-cause-analysis';
import { DeepDiveNoBiasDetectedSubTabState } from 'apps/sigma2-ui/src/focus-area/deep-dive/DeepDiveEmptyLoadingAndErrorStates';
import { DeepDiveTabsGraphDisplay } from 'apps/sigma2-ui/src/components/DeepDiveTabsGraphDisplay';
import { Nullable } from 'libs/end-to-end/src/ui-types';
import { DimensionDefinition } from 'libs/end-to-end/src/types/databases/dimensions/dimension-definition';
import { LevelDefinition } from 'libs/end-to-end/src/types/databases/dimensions/level-definition';
import { MyFocusAreaDefinition } from 'libs/end-to-end/src/my-focus-area-definition';
import { MyMetricDefinition } from 'libs/end-to-end/src/my-metric-definition';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { TouchTarget } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { PlayCircleFilled } from '@mui/icons-material';
import { useAnalytics } from 'apps/sigma2-ui/src/lib/hooks/useAnalytics';
import { getBiasTestIdFromRootCauseGroupResult } from 'libs/end-to-end/src/root-cause-analysis';
import { CalibrationFraming } from 'libs/end-to-end/src/calibration-framing';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';

export const DeepDiveProcessBiasTab: React.FC<{
  focusAreaDefinition: MyFocusAreaDefinition;
  metricDefinition: Nullable<MyMetricDefinition>;
  dimensionDefinition: Nullable<DimensionDefinition>;
  treatmentGroupLevelDefinition: Nullable<LevelDefinition>;
  referenceGroupLevelDefinition: Nullable<LevelDefinition>;
  groupResult: Nullable<RootCauseGroupResult>;
  graphRef: React.RefObject<HTMLInputElement>;
  carouselSwitcher: React.ReactNode;
  performanceMetricDefinition: Nullable<MyMetricDefinition>;
  organizationName: Nullable<string>;
  onViewBiasTestVideoClick: (id: number) => void;
  calibrationFraming: CalibrationFraming;
  // eslint-disable-next-line complexity
}> = ({
  focusAreaDefinition,
  metricDefinition,
  dimensionDefinition,
  treatmentGroupLevelDefinition,
  referenceGroupLevelDefinition,
  groupResult,
  carouselSwitcher,
  graphRef,
  performanceMetricDefinition,
  organizationName,
  onViewBiasTestVideoClick,
  calibrationFraming,
}) => {
  const analytics = useAnalytics();
  const language = useLanguage();

  useEffect(() => {
    analytics.trackEvent({
      eventName: 'Deep Dive Section Viewed',
      eventProperties: {
        selectedTab: 'Process Bias',
      },
    });
  }, [analytics]);

  const biasTestId = useMemo(() => {
    return getBiasTestIdFromRootCauseGroupResult(groupResult);
  }, [groupResult]);

  const rootCauseOutputs = useMemo(() => {
    return extractRootCauseTextOutputs(groupResult);
  }, [groupResult]);

  if (!metricDefinition) {
    return <></>;
  }

  if (!groupResult) {
    return (
      <DeepDiveNoBiasDetectedSubTabState
        comparisonGroupDisplayName={
          treatmentGroupLevelDefinition?.displayName ?? ''
        }
        metricDefinition={metricDefinition}
        calibrationFraming={calibrationFraming}
      />
    );
  }

  if (
    !metricDefinition ||
    !dimensionDefinition ||
    !treatmentGroupLevelDefinition ||
    !referenceGroupLevelDefinition ||
    !groupResult.visualizationAttributes ||
    !organizationName
  ) {
    return (
      <DeepDiveNoBiasDetectedSubTabState
        comparisonGroupDisplayName={
          treatmentGroupLevelDefinition?.displayName ?? ''
        }
        metricDefinition={metricDefinition}
        calibrationFraming={calibrationFraming}
      />
    );
  }

  return (
    <Spacing size={'2xl'} direction={'vertical'}>
      <DeepDiveTabsGraphDisplay
        calibrationFraming={calibrationFraming}
        metricDefinition={metricDefinition}
        treatmentGroupLevelDefinition={treatmentGroupLevelDefinition}
        referenceGroupLevelDefinition={referenceGroupLevelDefinition}
        dimensionDefinition={dimensionDefinition}
        carouselSwitcher={carouselSwitcher}
        graphRef={graphRef}
        focusAreaDefinition={focusAreaDefinition}
        performanceMetricDefinition={performanceMetricDefinition}
        groupResult={groupResult}
        graphType={'bias-test-visualization'}
        headlineTranslatedContent={language.headlineForBiasVisualization({
          textOutputs: rootCauseOutputs,
          focusAreaDefinition,
          metricDefinition,
          dimensionDefinition,
          treatmentLevelDefinition: treatmentGroupLevelDefinition,
          referenceLevelDefinition: referenceGroupLevelDefinition,
          failType: groupResult.visualizationAttributes.failStatusType,
          metricCalibrationFraming: groupResult.visualizationAttributes.framing,
          metricSkillCalibrationFraming:
            groupResult.visualizationAttributes.skillFraming,
        })}
        descriptionTranslatedContent={language.explanationForBiasVisualization({
          textOutputs: rootCauseOutputs,
          metricDefinition,
          dimensionDefinition,
          treatmentLevelDefinition: treatmentGroupLevelDefinition,
          referenceLevelDefinition: referenceGroupLevelDefinition,
          metricCalibrationFraming: groupResult.visualizationAttributes.framing,
          metricSkillCalibrationFraming:
            groupResult.visualizationAttributes.skillFraming,
          failType: groupResult.visualizationAttributes.failStatusType,
          taskMetricDisplayName:
            groupResult.visualizationAttributes.taskMetricDisplayName,
          taskMetricModifier:
            groupResult.visualizationAttributes.taskMetricDisplayModifier ??
            null,
          organizationName: organizationName,
          calibrationFraming,
          treatmentThresholdDetails:
            groupResult.plot_type !== 'BoxPlot'
              ? null
              : groupResult.treatment_threshold_details,
          referenceThresholdDetails:
            groupResult.plot_type !== 'BoxPlot'
              ? null
              : groupResult.reference_threshold_details,
        })}
      />

      {biasTestId !== undefined && calibrationFraming === 'bias' && (
        <TouchTarget
          content={() => {
            return (
              <Spacing
                size={'sm'}
                direction={'horizontal'}
                className={'text-royal-800 items-center'}
                as={'div'}
              >
                <PlayCircleFilled className="w-4" color={'inherit'} />
                <S2Typography
                  variant={'headline-7-alt'}
                  textColorVariant={'royal-800'}
                  translatedContent={language.learnMoreAboutThisBias()}
                />
              </Spacing>
            );
          }}
          behavior={{
            type: 'onClick',
            onClick: () => {
              onViewBiasTestVideoClick(biasTestId);
            },
          }}
        />
      )}
    </Spacing>
  );
};
